import { Component, ViewChild } from '@angular/core';
import { CamposComponent } from '../../../componentes/campos/campos.component';
import {
  LossRatioRequest,
  LossRatioResponse,
} from '../../../interfaces/reclamaciones.interface';
import { TableConfig } from '../../../interfaces/table-config.interface';
import { InsuranceTableComponent } from '../../../componentes/insurance-table/insurance-table.component';
import { ReclamacionesService } from '../../../services/reclamaciones.service';
import { AuthService } from '../../../services/auth.service';
import { ExportarComponent } from '../../../componentes/exportar/exportar.component';
import { Subscription } from 'rxjs';
import { ApiResponse } from '../../../interfaces/common.interfaces';
import { CommonModule } from '@angular/common';
import { SinestralidadFilter } from '../../../interfaces/filters.interfaces';
import {
  exportarDetallesPDF,
  exportarTotalesPDF,
} from '../../../helpers/exportarSinestralidad';
import { HttpResponse } from '@angular/common/http';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytics.service';

/**
 * Componente para gestionar la sinestralidad
 * Permite visualizar y exportar información sobre las siniestralidades del productor
 */
@Component({
  selector: 'app-sinestralidad',
  standalone: true,
  imports: [
    ExportarComponent,
    CamposComponent,
    CommonModule,
    InsuranceTableComponent,
  ],
  templateUrl: './sinestralidad.component.html',
  styleUrl: './sinestralidad.component.css',
})
export class SinestralidadComponent {
  insuranceData: LossRatioResponse[] = [];
  insuranceDataMapped: Record<
    string,
    {
      records: LossRatioResponse[];
      totalPeriodPremium: number;
      totalIncurred: number;
      totallossRatio: number;
    }
  > = {};
  mensajeNoData: string = 'Selecciona los campos que deseas buscar';
  errorStatus?: number;
  title: string = '';
  user: string | null = '';
  fecha1?: Date;
  fecha2?: Date;
  // Configuración de la tabla
  tableConfig: TableConfig = {
    columns: [
      { key: 'lob', label: 'Ramo', sortable: true },
      { key: 'slob', label: 'Subramo', sortable: true },
      { key: 'clientName', label: 'Nombre', sortable: true },
      { key: 'policyNumber', label: 'Poliza', sortable: true, type: 'poliza' },
      {
        key: 'periodPremium',
        label: 'Prima periodo',
        sortable: true,
        type: 'currency',
      },
      {
        key: 'totalIncurred',
        label: 'Monto incurrido',
        sortable: true,
        type: 'currency',
      },
      {
        key: 'lossRatio',
        label: 'Sinestralidad',
        sortable: true,
        type: 'percentage',
      },
      { key: 'lobDescription', label: 'Descripción', sortable: true },
    ],
    showTotals: true,
    totalFields: ['totalIncurred'],
  };

  // Referencia al componente de la tabla
  @ViewChild(InsuranceTableComponent) tablaComponent!: InsuranceTableComponent;

  private currentSubscription?: Subscription;

  /**
   * Constructor del componente
   * @param reclamacionesService Servicio para gestionar las reclamaciones
   * @param authService Servicio de autenticación
   */
  constructor(
    private reclamacionesService: ReclamacionesService,
    private authService: AuthService,
    private gAnalytics:GoogleAnalyticsService,
  ) {
    this.title = 'Sinestralidad';
    this.user = this.authService.getProducerName();
  }

  /**
   * Maneja los cambios en los filtros de búsqueda
   * Realiza la petición al servidor con los nuevos filtros
   * @param filters Filtros de búsqueda seleccionados
   */
  onFilterChange(filters: SinestralidadFilter): void {
    // Cancelar cualquier suscripción anterior
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
    }

    if (Object.keys(filters).length === 0) {
      this.insuranceData = [];
      this.mensajeNoData = 'Selecciona los campos que deseas buscar';
      this.errorStatus = undefined;
      return;
    }
    this.fecha1 = filters.DateFrom;
    this.fecha2 = filters.DateTo;

    const request: LossRatioRequest = {
      DateFrom: filters.DateFrom!,
      DateTo: filters.DateTo!,
      ProducerNumber: this.authService.getProducerNumber() || 0,
    };

    if (filters.clientCode) request.ClientCode = filters.clientCode;

    this.currentSubscription = this.reclamacionesService
      .getLossRatio(request)
      .subscribe({
        next: (response: HttpResponse<ApiResponse<LossRatioResponse[]>>) => {
          if (response.status === 200) {
            this.insuranceData = response.body?.data || [];
            this.insuranceDataMapped = this.mapData(response.body?.data || []);

              this.gAnalytics.trackEvent('consulta_siniestralidad',{
              page:'/siniestralidad',
              action:'Consulta siniestralidad'
            })
           
            this.errorStatus = undefined;
          } else if (response.status === 204) {
            this.insuranceData = [];
            this.mensajeNoData = 'No hay datos disponibles para mostrar';
          }
        },
        error: (error) => {
          console.error('Error al obtener las sinestralidades:', error);
          this.insuranceData = [];
          this.mensajeNoData =
            'No se han podido obtener los datos de la sinestralidad';
        },
      });
  }

  /**
   * Transforma los datos de siniestralidad en un formato agrupado por ramo
   * @param data Datos de siniestralidad a transformar
   * @returns Objeto con los datos agrupados por ramo
   */
  mapData(data: LossRatioResponse[]): Record<
    string,
    {
      records: LossRatioResponse[];
      totalPeriodPremium: number;
      totalIncurred: number;
      totallossRatio: number;
    }
  > {
    let dit: Record<
      string,
      {
        records: LossRatioResponse[];
        totalPeriodPremium: number;
        totalIncurred: number;
        totallossRatio: number;
      }
    > = {};

    for (let record of data) {
      if (!dit[record.lobDescription]) {
        // Si aún no esta el ramo del record en el listado
        dit = {
          ...dit,
          [record.lobDescription]: {
            records: [record],
            totalPeriodPremium: record.periodPremium,
            totalIncurred: record.totalIncurred,
            totallossRatio: record.lossRatio,
          },
        };
      } else {
        // Si esta el ramo del record en el listado
        dit[record.lobDescription].records.push(record);
        dit[record.lobDescription].totalPeriodPremium =
          dit[record.lobDescription].totalPeriodPremium + record.periodPremium;
        dit[record.lobDescription].totalIncurred =
          dit[record.lobDescription].totalIncurred + record.totalIncurred;
        dit[record.lobDescription].totallossRatio =
          dit[record.lobDescription].totallossRatio + record.lossRatio;
      }
    }

    return dit;
  }

  /**
   * Maneja la exportación de datos según el formato especificado
   * @param formato Formato de exportación ('pdf' o 'excel')
   */
  onExportar(formato: string): void {
    if (!Object.keys(this.insuranceDataMapped).length) return;

    const params = {
      data: this.insuranceDataMapped,
      usuario: this.user,
      fechas: { desde: this.fecha1!, hasta: this.fecha2! },
    };
    if (formato === 'pdf') {
      exportarDetallesPDF(params);
      exportarTotalesPDF(params);

        this.gAnalytics.trackEvent('siniestralidad_exportar',{
        page:'/siniestralidad',
        format:formato
      })

    } else {
      if (this.tablaComponent) {
        const headerInfo = {
          titulo: this.title,
          fecha1: this.fecha2!,

          lob: this.getLobName(),
          slob: this.getSlobName(),
          tableConfig: this.tableConfig,
        };

        
        this.gAnalytics.trackEvent('siniestralidad_exportar',{
        page:'/siniestralidad',
        format:formato
      })
        this.tablaComponent.exportarDatos(formato, headerInfo);
      }
    }
  }

  // Extrayendo los nombres de los ramos y subramos
  @ViewChild(CamposComponent) camposComponent!: CamposComponent;

  /**
   * Obtiene el nombre del ramo seleccionado
   * @returns Nombre del ramo o cadena vacía si no hay selección
   */
  private getLobName(): string {
    const selectedLob = this.camposComponent?.lobs.find(
      (lob) => lob.lobId === Number(this.camposComponent?.selectedLob)
    );
    return selectedLob?.lobName || '';
  }

  /**
   * Obtiene el nombre del subramo seleccionado
   * @returns Nombre del subramo o cadena vacía si no hay selección
   */
  private getSlobName(): string {
    const selectedSLob = this.camposComponent?.filteredSLobs.find(
      (slob) => slob.sLobId === Number(this.camposComponent?.selectedSLob)
    );
    return selectedSLob?.sLobName || '';
  }
}
